import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  XMarkIcon, 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  PlusIcon,
  MinusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useDocumentAPI } from '../DocumentAPI';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface DocumentVersioningModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  currentContent: string;
  onVersionDecision: (decision: VersionDecision) => void;
  documentTitle?: string;
}

interface VersionDecision {
  action: 'create_version' | 'overwrite_current' | 'cancel';
  versionDescription?: string;
  versionType: 'major' | 'minor' | 'patch';
  includeDiff: boolean;
  diffSummary?: string;
}

interface DiffResult {
  added: string[];
  removed: string[];
  modified: string[];
  summary: {
    linesAdded: number;
    linesRemoved: number;
    charactersAdded: number;
    charactersRemoved: number;
    changePercentage: number;
  };
}

interface VersioningState {
  selectedAction: 'create_version' | 'overwrite_current' | 'cancel';
  versionDescription: string;
  versionType: 'major' | 'minor' | 'patch';
  showDiff: boolean;
  showAdvancedOptions: boolean;
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Custom hook for managing document versioning logic
 */
const useDocumentVersioning = (documentId: string, isOpen: boolean) => {
  const documentAPI = useDocumentAPI();
  const [latestContent, setLatestContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !documentId) return;

    const loadDocumentData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const contentResponse = await documentAPI.getLexicalContent(documentId);
        setLatestContent(contentResponse.lexical_content.content || '');
      } catch (err) {
        console.warn('Could not load latest content:', err);
        setLatestContent('');
      } finally {
        setIsLoading(false);
      }
    };

    loadDocumentData();
  }, [isOpen, documentId, documentAPI]);

  return { latestContent, isLoading, error };
};

/**
 * Custom hook for diff calculation and analysis
 */
const useDiffAnalysis = (oldContent: string, newContent: string) => {
  const calculateDiff = useCallback((oldContent: string, newContent: string): DiffResult => {
    const oldLines = oldContent.split('\n');
    const newLines = newContent.split('\n');
    
    const added: string[] = [];
    const removed: string[] = [];
    const modified: string[] = [];
    
    const maxLines = Math.max(oldLines.length, newLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      const oldLine = oldLines[i] || '';
      const newLine = newLines[i] || '';
      
      if (oldLine !== newLine) {
        if (oldLine && !newLine) {
          removed.push(`Line ${i + 1}: ${oldLine}`);
        } else if (!oldLine && newLine) {
          added.push(`Line ${i + 1}: ${newLine}`);
        } else {
          modified.push(`Line ${i + 1}: "${oldLine}" → "${newLine}"`);
        }
      }
    }
    
    const linesAdded = added.length;
    const linesRemoved = removed.length;
    const charactersAdded = newContent.length - oldContent.length;
    const charactersRemoved = oldContent.length - newContent.length;
    const changePercentage = oldContent.length > 0 ? 
      Math.abs((charactersAdded + charactersRemoved) / oldContent.length * 100) : 0;
    
    return {
      added,
      removed,
      modified,
      summary: {
        linesAdded,
        linesRemoved,
        charactersAdded,
        charactersRemoved,
        changePercentage
      }
    };
  }, []);

  const diffResult = useMemo(() => {
    if (!oldContent || !newContent) return null;
    return calculateDiff(oldContent, newContent);
  }, [oldContent, newContent, calculateDiff]);

  const generateAutoDescription = useCallback((diff: DiffResult): string => {
    const { summary } = diff;
    
    if (summary.changePercentage < 1) {
      return 'Minor formatting and typo corrections';
    } else if (summary.changePercentage < 10) {
      return 'Content updates and revisions';
    } else if (summary.changePercentage < 25) {
      return 'Significant content changes and additions';
    } else {
      return 'Major content restructuring and updates';
    }
  }, []);

  return { diffResult, generateAutoDescription };
};

/**
 * Custom hook for versioning state management
 */
const useVersioningState = (diffResult: DiffResult | null) => {
  const [state, setState] = useState<VersioningState>({
    selectedAction: 'create_version',
    versionDescription: '',
    versionType: 'minor',
    showDiff: true,
    showAdvancedOptions: false,
  });

  // Auto-generate version description based on diff
  useEffect(() => {
    if (diffResult && !state.versionDescription) {
      const autoDescription = diffResult.summary.changePercentage < 1 
        ? 'Minor formatting and typo corrections'
        : diffResult.summary.changePercentage < 10
        ? 'Content updates and revisions'
        : diffResult.summary.changePercentage < 25
        ? 'Significant content changes and additions'
        : 'Major content restructuring and updates';
      
      setState(prev => ({ ...prev, versionDescription: autoDescription }));
    }
  }, [diffResult, state.versionDescription]);

  const updateState = useCallback((updates: Partial<VersioningState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  return { state, updateState };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Modal header component
 */
const ModalHeader: React.FC<{
  documentTitle: string;
  onClose: () => void;
}> = ({ documentTitle, onClose }) => (
  <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <DocumentTextIcon className="h-6 w-6 text-lex-accent" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Document Versioning Decision
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {documentTitle} - Choose how to handle your changes
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        aria-label="Close modal"
      >
        <XMarkIcon className="h-6 w-6" />
      </button>
    </div>
  </div>
);

/**
 * Loading state component
 */
const LoadingState: React.FC = () => (
  <div className="p-8 text-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent mx-auto"></div>
    <p className="mt-2 text-gray-600 dark:text-gray-400">Analyzing document changes...</p>
  </div>
);

/**
 * Error state component
 */
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="p-8 text-center">
    <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto" />
    <p className="mt-2 text-red-600 dark:text-red-400">{error}</p>
  </div>
);

/**
 * Change analysis component
 */
const ChangeAnalysis: React.FC<{ diffResult: DiffResult | null }> = ({ diffResult }) => {
  const hasChanges = diffResult && (
    diffResult.summary.linesAdded > 0 || 
    diffResult.summary.linesRemoved > 0 || 
    diffResult.summary.charactersAdded !== 0
  );

  return (
    <div className="bg-lex-accent-soft p-4 rounded-lg">
      <div className="flex items-center space-x-2 mb-3">
        <DocumentMagnifyingGlassIcon className="h-5 w-5 text-lex-accent" />
        <h4 className="font-medium text-gray-900 dark:text-white">Change Analysis</h4>
      </div>
      
      {hasChanges ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{diffResult?.summary.linesAdded || 0}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Lines Added</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{diffResult?.summary.linesRemoved || 0}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Lines Removed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-lex-accent">{diffResult?.summary.charactersAdded || 0}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Chars Added</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{diffResult?.summary.changePercentage.toFixed(1)}%</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Change</div>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">No changes detected in document content</p>
        </div>
      )}
    </div>
  );
};

/**
 * Action selection component
 */
const ActionSelection: React.FC<{
  selectedAction: VersioningState['selectedAction'];
  onActionSelect: (action: VersioningState['selectedAction']) => void;
  isSignificantChange: boolean;
  isMinorChange: boolean;
}> = ({ selectedAction, onActionSelect, isSignificantChange, isMinorChange }) => (
  <div className="space-y-4">
    <h4 className="font-medium text-gray-900 dark:text-white">Choose Your Action</h4>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div 
        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
          selectedAction === 'create_version'
            ? 'border-lex-accent bg-lex-accent-soft'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
        onClick={() => onActionSelect('create_version')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onActionSelect('create_version')}
      >
        <div className="flex items-center space-x-2 mb-2">
          <DocumentDuplicateIcon className="h-5 w-5 text-lex-accent" />
          <span className="font-medium text-gray-900 dark:text-white">Create New Version</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Save changes as a new version while preserving the current version
        </p>
        {isSignificantChange && (
          <div className="mt-2 flex items-center space-x-1">
            <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />
            <span className="text-xs text-orange-600 dark:text-orange-400">Recommended for significant changes</span>
          </div>
        )}
      </div>

      <div 
        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
          selectedAction === 'overwrite_current'
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
        onClick={() => onActionSelect('overwrite_current')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onActionSelect('overwrite_current')}
      >
        <div className="flex items-center space-x-2 mb-2">
          <ArrowPathIcon className="h-5 w-5 text-green-600" />
          <span className="font-medium text-gray-900 dark:text-white">Update Current</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Overwrite the current version without creating a new version
        </p>
        {isMinorChange && (
          <div className="mt-2 flex items-center space-x-1">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <span className="text-xs text-green-600 dark:text-green-400">Recommended for minor changes</span>
          </div>
        )}
      </div>

      <div 
        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
          selectedAction === 'cancel'
            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
        onClick={() => onActionSelect('cancel')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onActionSelect('cancel')}
      >
        <div className="flex items-center space-x-2 mb-2">
          <XMarkIcon className="h-5 w-5 text-red-600" />
          <span className="font-medium text-gray-900 dark:text-white">Cancel Changes</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Discard all changes and return to the previous state
        </p>
      </div>
    </div>
  </div>
);

/**
 * Version details component
 */
const VersionDetails: React.FC<{
  state: VersioningState;
  onStateUpdate: (updates: Partial<VersioningState>) => void;
}> = ({ state, onStateUpdate }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="font-medium text-gray-900 dark:text-white">Version Details</h4>
      <button
        onClick={() => onStateUpdate({ showAdvancedOptions: !state.showAdvancedOptions })}
        className="flex items-center space-x-1 text-sm text-lex-accent hover:text-lex-accent-hover text-lex-accent"
      >
        {state.showAdvancedOptions ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        <span>Advanced Options</span>
      </button>
    </div>

    {/* Version Type Selection */}
    <div className="grid grid-cols-3 gap-4">
      {(['major', 'minor', 'patch'] as const).map((type) => (
        <div 
          key={type}
          className={`p-3 border rounded-lg cursor-pointer text-center transition-colors ${
            state.versionType === type
              ? type === 'major' 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                : type === 'minor'
                ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                : 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => onStateUpdate({ versionType: type })}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onStateUpdate({ versionType: type })}
        >
          <div className={`text-lg font-bold ${
            type === 'major' ? 'text-red-600' : type === 'minor' ? 'text-yellow-600' : 'text-green-600'
          }`}>
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {type === 'major' ? 'Breaking changes' : type === 'minor' ? 'New features' : 'Bug fixes'}
          </div>
        </div>
      ))}
    </div>

    {/* Version Description */}
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Version Description
      </label>
      <textarea
        value={state.versionDescription}
        onChange={(e) => onStateUpdate({ versionDescription: e.target.value })}
        placeholder="Describe what changed in this version..."
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-transparent dark:bg-gray-700 dark:text-white"
        rows={3}
      />
    </div>

    {/* Advanced Options */}
    {state.showAdvancedOptions && (
      <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <EyeIcon className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Include Change Summary</span>
          </div>
          <button
            onClick={() => onStateUpdate({ showDiff: !state.showDiff })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              state.showDiff ? 'bg-lex-accent' : 'bg-gray-200 dark:bg-gray-600'
            }`}
            role="switch"
            aria-checked={state.showDiff}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                state.showDiff ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Include a detailed summary of changes in the version history
        </p>
      </div>
    )}
  </div>
);

/**
 * Change preview component
 */
const ChangePreview: React.FC<{ diffResult: DiffResult | null; showDiff: boolean }> = ({ diffResult, showDiff }) => {
  const hasChanges = diffResult && (
    diffResult.summary.linesAdded > 0 || 
    diffResult.summary.linesRemoved > 0 || 
    diffResult.summary.charactersAdded !== 0
  );

  if (!showDiff || !diffResult || !hasChanges) return null;

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900 dark:text-white">Change Preview</h4>
      
      <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        {diffResult.added.length > 0 && (
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <PlusIcon className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Added ({diffResult.added.length})</span>
            </div>
            <div className="space-y-1">
              {diffResult.added.slice(0, 5).map((line, index) => (
                <div key={index} className="text-sm bg-green-50 dark:bg-green-900/20 p-2 rounded">
                  <span className="text-green-700 dark:text-green-300">+ {line}</span>
                </div>
              ))}
              {diffResult.added.length > 5 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ... and {diffResult.added.length - 5} more additions
                </div>
              )}
            </div>
          </div>
        )}

        {diffResult.removed.length > 0 && (
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <MinusIcon className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-700 dark:text-red-300">Removed ({diffResult.removed.length})</span>
            </div>
            <div className="space-y-1">
              {diffResult.removed.slice(0, 5).map((line, index) => (
                <div key={index} className="text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  <span className="text-red-700 dark:text-red-300">- {line}</span>
                </div>
              ))}
              {diffResult.removed.length > 5 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ... and {diffResult.removed.length - 5} more removals
                </div>
              )}
            </div>
          </div>
        )}

        {diffResult.modified.length > 0 && (
          <div className="p-3">
            <div className="flex items-center space-x-2 mb-2">
              <ArrowPathIcon className="h-4 w-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Modified ({diffResult.modified.length})</span>
            </div>
            <div className="space-y-1">
              {diffResult.modified.slice(0, 3).map((line, index) => (
                <div key={index} className="text-sm bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                  <span className="text-yellow-700 dark:text-yellow-300">~ {line}</span>
                </div>
              ))}
              {diffResult.modified.length > 3 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ... and {diffResult.modified.length - 3} more modifications
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Warning component for significant changes
 */
const SignificantChangeWarning: React.FC<{ 
  isSignificantChange: boolean; 
  selectedAction: VersioningState['selectedAction']; 
  changePercentage: number;
}> = ({ isSignificantChange, selectedAction, changePercentage }) => {
  if (!isSignificantChange || selectedAction !== 'overwrite_current') return null;

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
      <div className="flex items-center space-x-2">
        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
        <span className="font-medium text-yellow-800 dark:text-yellow-200">Significant Changes Detected</span>
      </div>
      <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
        You're about to overwrite a document with significant changes ({changePercentage.toFixed(1)}% change). 
        Consider creating a new version instead to preserve the current version.
      </p>
    </div>
  );
};

/**
 * Action buttons component
 */
const ActionButtons: React.FC<{
  selectedAction: VersioningState['selectedAction'];
  isProcessing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ selectedAction, isProcessing, onCancel, onConfirm }) => (
  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
    <button
      onClick={onCancel}
      className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
      disabled={isProcessing}
    >
      Cancel
    </button>
    <button
      onClick={onConfirm}
      disabled={isProcessing}
      className={`px-6 py-2 text-white rounded-lg flex items-center space-x-2 ${
        selectedAction === 'create_version'
          ? 'bg-lex-accent hover:bg-lex-accent-hover'
          : selectedAction === 'overwrite_current'
          ? 'bg-green-600 hover:bg-green-700'
          : 'bg-red-600 hover:bg-red-700'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {isProcessing ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>Processing...</span>
        </>
      ) : (
        <>
          {selectedAction === 'create_version' && <DocumentDuplicateIcon className="h-4 w-4" />}
          {selectedAction === 'overwrite_current' && <ArrowPathIcon className="h-4 w-4" />}
          {selectedAction === 'cancel' && <XMarkIcon className="h-4 w-4" />}
          <span>
            {selectedAction === 'create_version' && 'Create Version'}
            {selectedAction === 'overwrite_current' && 'Update Document'}
            {selectedAction === 'cancel' && 'Discard Changes'}
          </span>
        </>
      )}
    </button>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function DocumentVersioningModal({
  isOpen,
  onClose,
  documentId,
  currentContent,
  onVersionDecision,
  documentTitle = 'Document'
}: DocumentVersioningModalProps): React.JSX.Element | null {
  // Custom hooks
  const { latestContent, isLoading, error } = useDocumentVersioning(documentId, isOpen);
  const { diffResult } = useDiffAnalysis(latestContent, currentContent);
  const { state, updateState } = useVersioningState(diffResult);
  
  // Local state
  const [isProcessing, setIsProcessing] = useState(false);

  // Computed values
  const isSignificantChange = diffResult && diffResult.summary.changePercentage > 5;
  const isMinorChange = diffResult && diffResult.summary.changePercentage <= 5;

  // Event handlers
  const handleVersionDecision = async () => {
    if (state.selectedAction === 'cancel') {
      onVersionDecision({ action: 'cancel', versionType: 'minor', includeDiff: false });
      return;
    }

    setIsProcessing(true);
    
    try {
      const decision: VersionDecision = {
        action: state.selectedAction,
        versionDescription: state.versionDescription.trim() || 'Content updated',
        versionType: state.versionType,
        includeDiff: state.showDiff,
        diffSummary: diffResult ? `${diffResult.summary.linesAdded} lines added, ${diffResult.summary.linesRemoved} lines removed` : undefined
      };

      onVersionDecision(decision);
      
    } catch (error) {
      console.error('Error processing version decision:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity lex-overlay-enter" onClick={onClose} />

        {/* Modal panel */}
        <div className="relative w-full max-w-4xl bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
          <ModalHeader documentTitle={documentTitle} onClose={onClose} />

          {/* Content */}
          <div className="bg-white dark:bg-gray-900">
            {isLoading ? (
              <LoadingState />
            ) : error ? (
              <ErrorState error={error} />
            ) : (
              <div className="p-6 space-y-6">
                <ChangeAnalysis diffResult={diffResult} />
                
                <ActionSelection
                  selectedAction={state.selectedAction}
                  onActionSelect={(action) => updateState({ selectedAction: action })}
                  isSignificantChange={!!isSignificantChange}
                  isMinorChange={!!isMinorChange}
                />

                {/* Version Details (only show for create_version) */}
                {state.selectedAction === 'create_version' && (
                  <VersionDetails state={state} onStateUpdate={updateState} />
                )}

                {/* Change Preview */}
                <ChangePreview diffResult={diffResult} showDiff={state.showDiff} />

                {/* Warning for significant changes */}
                <SignificantChangeWarning
                  isSignificantChange={!!isSignificantChange}
                  selectedAction={state.selectedAction}
                  changePercentage={diffResult?.summary.changePercentage || 0}
                />

                {/* Action Buttons */}
                <ActionButtons
                  selectedAction={state.selectedAction}
                  isProcessing={isProcessing}
                  onCancel={onClose}
                  onConfirm={handleVersionDecision}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 